import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sunrise, MessageCircle, SlidersHorizontal, Bike, Car, Wallet, Clock } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { scrollTo } from "@/components/Nav";
import { ShareButton } from "@/components/Logo";

const rupees = (n) => `₹${n.toLocaleString("en-IN")}`;

export default function Itineraries() {
  const [PLANS, setPlans] = useState([]);
  const [key, setKey] = useState("");
  useEffect(() => {
    api.get("/itineraries").then((r) => { setPlans(r.data); if (r.data[0]) setKey(r.data[0].key); }).catch(() => {});
  }, []);
  const plan = PLANS.find((p) => p.key === key);
  if (!plan) return null;
  const total = plan.budget.reduce((s, b) => s + Number(b.amount || 0), 0);
  const waMsg = `Hi Random Rider! I'd like to book your "${plan.title}" ${plan.label} plan (approx ${rupees(total)} for two). Please share availability.`;

  return (
    <section id="itineraries" className="py-20 lg:py-28 bg-ink text-sand relative overflow-hidden" data-testid="itineraries-section">
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-foam/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-3">04 · Ready-Made Plans</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Don't plan. <span className="italic text-terra">Just ride.</span>
          </h2>
          <p className="mt-4 text-sand/70 leading-relaxed">Hour-by-hour itineraries built from our places guide, with an honest budget for two — pick one and send it to us.</p>
        </motion.div>

        <div className="flex gap-2 mb-8" data-testid="itinerary-tabs">
          {PLANS.map((p) => (
            <button key={p.key} onClick={() => setKey(p.key)} data-testid={`itinerary-tab-${p.key}`}
              className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-colors ${key === p.key ? "bg-terra text-sand" : "bg-sand/10 text-sand/70 hover:bg-sand/20"}`}>
              {p.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={plan.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-testid={`itinerary-plan-${plan.key}`}>
            <div className="lg:col-span-8">
              <h3 className="font-serif text-2xl sm:text-3xl font-bold" data-testid="itinerary-title">{plan.title}</h3>
              <p className="text-sand/60 mt-1 mb-6">{plan.tagline}</p>
              <div className={`grid gap-5 ${plan.days.length > 1 ? "md:grid-cols-2 xl:grid-cols-3" : ""}`}>
                {plan.days.map((d, di) => (
                  <div key={d.label} className="bg-white/[0.06] border border-sand/10 rounded-2xl p-5" data-testid={`itinerary-day-${di + 1}`}>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-gold flex items-center gap-1.5 mb-4"><Sunrise size={12} /> {d.label}</p>
                    <ol className="relative border-l border-sand/15 ml-2 space-y-4">
                      {d.stops.map((s) => (
                        <li key={s.time + s.place} className="pl-5 relative">
                          <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-terra" />
                          <p className="text-[11px] font-mono text-sand/50 flex items-center gap-1"><Clock size={10} /> {s.time}</p>
                          <p className="text-sm font-semibold leading-snug">{s.place}</p>
                          <p className="text-xs text-sand/60 leading-relaxed">{s.note}</p>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4 bg-sand text-ink rounded-3xl p-6 sm:p-7 self-start lg:sticky lg:top-24" data-testid="itinerary-budget">
              <p className="font-mono text-[11px] uppercase tracking-widest text-terra flex items-center gap-1.5"><Wallet size={13} /> Budget for two</p>
              <div className="mt-4 space-y-2.5">
                {plan.budget.map((b) => (
                  <div key={b.label} className="flex justify-between gap-3 text-sm border-b border-ink/10 pb-2">
                    <span className="text-ink/70">{b.label}</span><span className="font-semibold shrink-0">{Number(b.amount) ? rupees(Number(b.amount)) : "Free"}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-end justify-between">
                <p className="text-xs font-mono uppercase tracking-widest text-ink/50">Approx total</p>
                <p className="font-serif text-4xl font-black text-terra" data-testid="itinerary-total">{rupees(total)}</p>
              </div>
              <p className="mt-3 text-xs text-ink/60 flex items-start gap-1.5">{plan.vehicle.startsWith("AC") ? <Car size={13} className="shrink-0 mt-0.5" /> : <Bike size={13} className="shrink-0 mt-0.5" />} {plan.vehicle}</p>
              <a href={waLink(waMsg)} target="_blank" rel="noreferrer" data-testid="itinerary-whatsapp-button"
                className="mt-5 flex items-center justify-center gap-2 w-full bg-[#25D366] text-white text-sm font-semibold py-3 rounded-full hover:brightness-110 transition-all">
                <MessageCircle size={15} /> Book this plan on WhatsApp
              </a>
              <button onClick={() => scrollTo("#package")} data-testid="itinerary-customise-button"
                className="mt-2 flex items-center justify-center gap-2 w-full border border-ink/15 text-ink text-sm font-semibold py-3 rounded-full hover:bg-ink hover:text-sand transition-colors">
                <SlidersHorizontal size={15} /> Customise in Package Builder
              </button>
              <ShareButton kind="itineraries" slug={plan.key} title={plan.title} label="Share this plan" testId={`itinerary-share-${plan.key}`}
                className="mt-2 w-full text-xs font-semibold text-ink/60 hover:text-terra py-2" />
            </aside>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
